<script lang="ts" setup="">
import type { NovoIconNames } from '@novonordisk/studyhub-novoui/vue'
import { NIcon } from '@novonordisk/studyhub-novoui/vue'
import { computed } from 'vue'

export type NotificationVariant = 'info' | 'success' | 'warning' | 'danger' | 'white' | 'true-blue'

const {
  variant = 'true-blue',
  elevated = false,
  icon = undefined,
  title = undefined,
  text = undefined,
  closable = false,
  clickable = false,
} = defineProps<{
  variant?: NotificationVariant
  elevated?: boolean
  icon?: NovoIconNames
  title?: string
  text?: string
  closable?: boolean
  clickable?: boolean
}>()

defineEmits<{
  (e: 'close'): void
  (e: 'click'): void
}>()

defineSlots<{
  default?: void
  text?: void
  title?: void
  actions?: void
}>()

const backgroundColor = computed((): string => {
  switch (variant) {
    case 'info':
      return '#D7EAF8'
    case 'success':
      return '#D8EBD6'
    case 'warning':
      return '#FAEECC'
    case 'danger':
      return '#FADDD8'
    case 'white':
      return '#FFFFFF'
    case 'true-blue':
    default:
      return '#001965'
  }
})

const appendIcon = computed((): NovoIconNames => {
  if (icon) {
    return icon
  }

  switch (variant) {
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    case 'danger':
      return 'alert'
    case 'info':
    case 'white':
    case 'true-blue':
    default:
      return 'information'
  }
})
</script>

<template>
  <div
    :class="{
      'shadow-lg': elevated,
      'cursor-pointer': clickable,
      'hover:shadow-xl': clickable && elevated,
    }"
    :style="{ backgroundColor }"
    class="w-[500px] rounded p-4 transition-all duration-150 ease-in-out"
    data-test="notification"
    @click="clickable ? $emit('click') : () => {}"
  >
    <div
      :class="{
        'items-start': (!$slots.text && !text) || (!$slots.title && !title),
      }"
      class="flex"
      data-test="notification-content"
    >
      <div
        class="mr-4 shrink-0"
        data-test="notification-icon-wrapper"
      >
        <NIcon
          :class="`fill-${variant === 'true-blue' ? 'white' : 'true-blue'}`"
          :icon="appendIcon"
          :size="20"
          data-test="notification-icon"
        />
      </div>
      <div
        class="flex-1"
        data-test="notification-text-content"
      >
        <p
          :class="[`text-${variant === 'true-blue' ? 'white' : 'true-blue'}`, { 'mb-1': $slots.text || text }]"
          class="text-headings-h5 font-bold"
          data-test="notification-title"
        >
          {{ title }}
        </p>
        <p
          v-if="$slots.default || text"
          :class="[
            `text-${variant === 'true-blue' ? 'white' : 'true-blue'}`,
            {
              '-mt-1': !$slots.title && !title,
            },
          ]"
          class="text-body-small"
          data-test="notification-text"
        >
          <slot>
            {{ text }}
          </slot>
        </p>
      </div>
      <div
        v-if="closable"
        class="ml-4 shrink-0"
        data-test="notification-close-wrapper"
      >
        <button
          class="flex size-6 items-center justify-center"
          data-test="notification-close-button"
          @click="$emit('close')"
        >
          <NIcon
            :class="`fill-${variant === 'true-blue' ? 'white' : 'true-blue'}`"
            icon="close"
            variant="small"
            :size="16"
            data-test="notification-close-icon"
          />
        </button>
      </div>
    </div>
    <div
      v-if="$slots.actions"
      class="mt-1 flex justify-end"
      data-test="notification-actions"
    >
      <slot name="actions" />
    </div>
  </div>
</template>
